import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, Observable, shareReplay, startWith, Subject, takeUntil, tap } from 'rxjs';
import { FIELDS_MAP, IFinancialField } from 'src/app/interfaces/finalcial-field.interface';

@Component({
  selector: 'app-tabbed-accordion',
  templateUrl: './tabbed-accordion.component.html',
  styleUrls: ['./tabbed-accordion.component.scss']
})
export class TabbedAccordionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public tabbedAccordionTitle!: string;
  @Input() public type!: string;
  @Input() public tabTitle!: string;
  @Input() public storageKey!: string;
  @Input() public singleInput: boolean = false;
  @Input() public singleInputLabel: string = 'Total gasto único';
  @Input() public customHeaderClass: string = '';
  @Input() form!: FormGroup;
  @Input() isSaveForm: boolean = false;
  @Input() isMark: boolean = false;
  @Input() isLoadForm: boolean = false;
  @Input() set reset$(subject: Subject<void> | null) {
    if (subject) {
      subject.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.form.reset();
      });
    }
  }

  @Output() totalChange = new EventEmitter<number>();

  public readonly tabbedAccordionId: string;
  public readonly tabId: string;
  public activeTab: number = 1;
  public detailedFields: IFinancialField[];
  public total$!: Observable<number>;

  private destroy$ = new Subject<void>();

  constructor(private readonly fb: FormBuilder) {
    this.tabbedAccordionId = this.generateSafeId('acc');
    this.tabId = this.generateSafeId('tab');
    this.detailedFields = [];
  }

  ngOnInit(): void {
    this.listenTotaChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private generateSafeId(prefix: string): string {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type'] && changes['type'].currentValue) {
      this.detailedFields = this.setDetailFields();
      this.buildDetailedForm();
    }
  }

  public get accordionIcon(): string {
    switch (this.type) {
      case 'owner': return '/assets/svg/icon_titular.svg'
      case 'spouse': return '/assets/svg/icon_conyugue.svg';
      case 'home': return '/assets/svg/icon_casa.svg';
      case 'recreation': return '/assets/svg/icon_recreacion.svg';
      case 'mobility': return '/assets/svg/icon_movilidad.svg';
      case 'health': return '/assets/svg/icon_salud.svg';
      case 'education': return '/assets/svg/icon_educacion.svg';
      case 'trips': return '/assets/svg/icon_viajes.svg';
      case 'loans': return '/assets/svg/icon_prestamos.svg';
      case 'extras': return '/assets/svg/icon_otros.svg';
      default: return ''
    }
  }

  public onSelectTab(tab: number): void {
    this.activeTab = tab;
    const firstControlKey = Object.keys(this.form.controls)[0];

    if (this.activeTab === 2) {
      this.form.enable();
      this.form.controls[firstControlKey].disable();
    } else {
      const total = this.setDetailFields().slice(1, this.detailedFields.length).reduce((sum, field) => sum + Number(this.form.get(field.key)?.value || 0), 0);
      this.form.get(firstControlKey)?.setValue(total || this.form.get(firstControlKey)?.value);
      this.form.disable();
      this.form.controls[firstControlKey].enable();
    }
    this.form.reset();
  }

  get hasValues(): boolean {
    return Object.values(this.form.value).some(v => v !== null && v !== '');
  }

  isMarkField(field: string): boolean {
    let fields: string[] = [];

    switch (this.isMark && this.type) {
      case 'owner':
        fields = ['income1'];
        break;
      case 'spouse':
        fields = ['income1'];
        break;
      case 'home':
        fields = ['expense1'];
        break;
      case 'health':
        fields = this.activeTab === 1 ? ['expense1'] : ['expense3', 'expense4', 'expense5'];
        break;
      case 'recreation':
        fields = ['expense1', 'expense2', 'expense3', 'expense4', 'expense5'];
        break;
      case 'trips':
        fields = ['expense1', 'expense2', 'expense3', 'expense4', 'expense5', 'expense6'];
        break;
      default:
        return false;
    }

    return fields.some(f => f === field);
  }

  isMarkTotal(): boolean {

    switch (this.isMark && this.type) {
      case 'owner':
        return true;
      case 'spouse':
        return true;
      case 'home':
        return true;
      case 'health':
        return true;
      case 'recreation':
        return true;
      case 'trips':
        return true;
      default:
        return false;
    }
  }

  allowNumbersOnly(event: KeyboardEvent) {
    const char = event.key;
    if (!/[0-9]/.test(char)) {
      event.preventDefault();
    }
  }

  cleanNonNumbers(event: any) {
    const input = event.target;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.form.get('numero')?.setValue(input.value, { emitEvent: false });
  }

  get totalLabel(): string {
    switch (this.type) {
      case 'nonProductiveAssets': return 'Total de activos no productivos';
      case 'productiveAssets': return 'Total de activos productivos';
      case 'liabilities': return 'Total de pasivos';
      default: return `Total de ${this.tabTitle} mensuales`;
    }
  }

  private setDetailFields(): IFinancialField[] {
    return FIELDS_MAP(this.type);
  }

  private buildDetailedForm(): void {
    const group: any = {};

    this.detailedFields.forEach(field => {
      group[field.key] = [{ value: '', disabled: field.disabled ?? false }];
    });

    this.form = this.fb.group(group);
    /* if (this.isLoadForm) {
      this.loadFormValue();
    } */
    this.loadFormValue();
  }

  private listenTotaChange() {
    this.total$ = this.form.valueChanges.pipe(
      startWith(this.form.value),
      tap(values => {
        localStorage.setItem(`${this.storageKey || this.type}Form`, JSON.stringify(values));
      }),
      map(values =>
        Object.values(values)
          .map(v => Number(v))
          .reduce((sum, current) => sum + current, 0)
      ),
      shareReplay(1),
      takeUntil(this.destroy$)
    );
    this.total$.subscribe(total => {
      if (this.type === 'trips') {
        this.totalChange.emit(total / 12);
      } else {
        this.totalChange.emit(total);
      }
    });
  }

  private loadFormValue(): void {
    const key = this.storageKey || this.type;
    const form = JSON.parse(localStorage.getItem(`${key}Form`) || '{}');
    const hasDetailValues = this.hasValuesStorage(form);

    if (hasDetailValues) {
      // Tiene valores detallados → activar tab detallado
      this.form.enable();
      const firstControlKey = Object.keys(this.form.controls)[0];
      this.form.patchValue(form);
      const total = this.setDetailFields().slice(1).reduce((sum, field) => sum + Number(form[field.key] || 0), 0);
      this.form.get(firstControlKey)?.setValue(total);
      // Deshabilitar el primer campo (total) y activar tab 2
      this.activeTab = 2;
      this.form.controls[firstControlKey].disable();
    } else if (form[Object.keys(form)[0]]) {
      // Solo tiene el campo total → activar tab 1
      this.form.patchValue(form);
    }
  }

  private getFormStorage(formName: string) {
    return JSON.parse(localStorage.getItem(formName) || '{}')
  }

  private hasValuesStorage(form: Record<string, any>): boolean {
    return this.setDetailFields().slice(1).some(field => {
      const value = form[field.key];
      return value !== null && value !== undefined && value.toString().trim() !== '';
    });
  }

}
